/**
 * Payroll (Puantaj + Maaş Hesaplama) — Faz 1 MVP.
 *
 *  - PayrollTimesheetPage  → /puantaj/aylik   (aylık puantaj — PDKS'den otomatik + manuel override)
 *  - PayrollSalaryPage     → /maas/hesaplama  (maaş hesaplama, avans/prim, onay/kilit)
 *  - EmployeeWageCard      → EmployeeProfile içinde "Ücret Bilgileri" sekmesi
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ClipboardList, Wallet, Play, Lock, Unlock, RefreshCw, Save,
  Plus, Trash2, Calculator, FileText, Edit3, CheckCircle2, User,
  ChevronRight, Loader2,
} from "lucide-react";
import { toast } from "sonner";

function todayIso() {
  const d = new Date(); return d.toISOString().slice(0, 10);
}
function monthIso(d = new Date()) {
  return d.toISOString().slice(0, 7);
}
function fmtTRY(v) {
  const n = Number(v || 0);
  return n.toLocaleString("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 });
}
function fmtTRYExact(v) {
  const n = Number(v || 0);
  return n.toLocaleString("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 2 });
}

async function downloadReport(month, kind) {
  try {
    const res = await api.get(`/payroll/runs/${month}/export.${kind}`, { responseType: "blob" });
    const blob = new Blob([res.data], {
      type: kind === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `puantaj_${month}.${kind}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast.error(formatApiError(e));
  }
}

function EmployeeAvatar({ emp, size = "sm" }) {
  const s = size === "sm" ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm";
  const initials = (emp?.name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (emp?.photo_url) return <img src={emp.photo_url} alt="" className={`${s} rounded-full object-cover ring-1 ring-slate-700`} />;
  return (
    <div className={`${s} rounded-full bg-slate-800 text-slate-300 ring-1 ring-slate-700 flex items-center justify-center font-mono uppercase`}>
      {initials || "?"}
    </div>
  );
}

// ==========================================================================
// 1) AYLIK PUANTAJ
// ==========================================================================
export function PayrollTimesheetPage() {
  const [month, setMonth] = useState(monthIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overrides, setOverrides] = useState([]);
  const [editing, setEditing] = useState(null); // {emp, field, current}

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [{ data: t }, { data: ov }] = await Promise.all([
        api.get("/payroll/timesheet", { params: { month } }),
        api.get(`/payroll/timesheet/${month}/overrides`),
      ]);
      setData(t);
      setOverrides(ov?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <PageHeader title="Aylık Puantaj" subtitle="PDKS'den otomatik doldurulur · manuel düzeltmeler kayıt altına alınır" />

      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="bg-slate-950 border-slate-700 rounded-sm w-40" data-testid="timesheet-month" />
        <Button variant="outline" size="sm" onClick={load} className="border-slate-700 rounded-sm"
          data-testid="timesheet-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Yenile
        </Button>
        {overrides.length > 0 && (
          <Badge className="bg-amber-950 text-amber-300 border-amber-800 rounded-sm text-[10px] uppercase">
            {overrides.length} manuel düzeltme
          </Badge>
        )}
      </div>

      <Card className="bg-slate-950/60 border-slate-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-800">
            <tr>
              <th className="text-left px-3 py-2">Personel</th>
              <th className="text-left px-3 py-2">Departman</th>
              <th className="text-right px-3 py-2">Çalışılan</th>
              <th className="text-right px-3 py-2">Eksik</th>
              <th className="text-right px-3 py-2">Ücretli İzin</th>
              <th className="text-right px-3 py-2">Ücretsiz İzin</th>
              <th className="text-right px-3 py-2">Rapor</th>
              <th className="text-right px-3 py-2">Fazla Mesai</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.employee.id} className="border-b border-slate-900 hover:bg-slate-900/40" data-testid={`ts-row-${r.employee.id}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <EmployeeAvatar emp={r.employee} />
                    <span className="text-slate-100">{r.employee.name}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-slate-400">{r.employee.department || "—"}</td>
                <NumCell v={r.worked_days} testId={`ts-worked-${r.employee.id}`} unit="gün" />
                <NumCell v={r.missing_days} testId={`ts-missing-${r.employee.id}`} unit="gün" tone={r.missing_days > 0 ? "rose" : ""} />
                <NumCell v={r.paid_leave_days} testId={`ts-paid-${r.employee.id}`} unit="gün" />
                <NumCell v={r.unpaid_leave_days} testId={`ts-unpaid-${r.employee.id}`} unit="gün" tone={r.unpaid_leave_days > 0 ? "amber" : ""} />
                <NumCell v={r.sick_days} testId={`ts-sick-${r.employee.id}`} unit="gün" />
                <NumCell v={`${(r.overtime_minutes / 60).toFixed(1)}`} testId={`ts-ot-${r.employee.id}`} unit="sa" tone={r.overtime_minutes > 0 ? "emerald" : ""} />
                <td className="px-3 py-2 text-right">
                  <TimesheetEditMenu row={r} month={month} onSaved={load} />
                </td>
              </tr>
            ))}
            {(!data || data.rows.length === 0) && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">Kayıt yok</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {overrides.length > 0 && (
        <Card className="bg-slate-950/60 border-slate-800 p-3" data-testid="ts-overrides-panel">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Son Manuel Düzeltmeler</div>
          <div className="space-y-1 text-xs max-h-64 overflow-y-auto">
            {overrides.slice(0, 20).map((o) => (
              <div key={o.id} className="border border-amber-950 bg-amber-950/20 rounded-sm px-2 py-1 font-mono">
                <span className="text-amber-300">{new Date(o.changed_at).toLocaleString("tr-TR")}</span>
                {" · "}<span className="text-slate-300">{o.changed_by_name}</span>
                {" · "}<b>{o.field}</b>: {String(o.old)} → <span className="text-emerald-300">{String(o.new)}</span>
                {o.note && <span className="text-slate-500"> · {o.note}</span>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function NumCell({ v, unit, tone, testId }) {
  const t = tone === "rose" ? "text-rose-300" : tone === "amber" ? "text-amber-300" : tone === "emerald" ? "text-emerald-300" : "text-slate-200";
  return (
    <td className="px-3 py-2 text-right font-mono tabular-nums" data-testid={testId}>
      <span className={t}>{v}</span> <span className="text-[10px] text-slate-500">{unit}</span>
    </td>
  );
}

function TimesheetEditMenu({ row, month, onSaved }) {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState("missing_days");
  const [value, setValue] = useState(0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const openFor = () => {
    setField("missing_days");
    setValue(row.missing_days);
    setNote("");
    setOpen(true);
  };

  const save = async () => {
    try {
      setSaving(true);
      await api.post(`/payroll/timesheet/${month}/override`, {
        employee_id: row.employee.id, field, new_value: Number(value), note,
      });
      toast.success("Kaydedildi");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="border-slate-700 rounded-sm h-7"
        onClick={openFor} data-testid={`ts-edit-${row.employee.id}`}>
        <Edit3 className="w-3 h-3 mr-1" /> Düzelt
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-950 border-slate-800">
          <DialogHeader>
            <DialogTitle>{row.employee.name} — Puantaj Düzeltme</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Yaptığınız her değişiklik kim/ne zaman/eski/yeni değerleriyle audit'e yazılır.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Alan</Label>
              <Select value={field} onValueChange={(v) => {
                setField(v);
                const key = v.replace("overtime_minutes", "overtime_minutes");
                setValue(row[key] ?? 0);
              }}>
                <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="ts-edit-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-700">
                  <SelectItem value="worked_days">Çalışılan Gün</SelectItem>
                  <SelectItem value="missing_days">Eksik Gün</SelectItem>
                  <SelectItem value="paid_leave_days">Ücretli İzin</SelectItem>
                  <SelectItem value="unpaid_leave_days">Ücretsiz İzin</SelectItem>
                  <SelectItem value="sick_days">Rapor</SelectItem>
                  <SelectItem value="overtime_minutes">Fazla Mesai (dk)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Yeni Değer</Label>
              <Input type="number" value={value} onChange={(e) => setValue(e.target.value)}
                className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="ts-edit-value" />
            </div>
            <div>
              <Label className="text-xs">Açıklama</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="ör. Rapor gecikmeli geldi"
                className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="ts-edit-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-700 rounded-sm" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" onClick={save} disabled={saving} data-testid="ts-edit-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Kaydet</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ==========================================================================
// 2) MAAŞ HESAPLAMA
// ==========================================================================
export function PayrollSalaryPage() {
  const [month, setMonth] = useState(monthIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailEmp, setDetailEmp] = useState(null);
  const [advOpen, setAdvOpen] = useState(false);
  const [bonusOpen, setBonusOpen] = useState(false);

  const run = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.post("/payroll/calculate", null, { params: { month } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { run(); }, [run]);

  const status = data?.status || "draft";
  const locked = status === "approved" || status === "locked";
  const totals = data?.totals || {};

  const approve = async () => {
    if (!window.confirm(`${month} ayını onaylamak istediğinize emin misiniz? Onaylandıktan sonra ay kilitlenir.`)) return;
    try {
      await api.post(`/payroll/runs/${month}/approve`, { note: "" });
      toast.success("Ay onaylandı ve kilitlendi");
      run();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unlock = async () => {
    const reason = window.prompt("Kilit açma gerekçesi:");
    if (!reason || reason.length < 3) { toast.error("Gerekçe en az 3 karakter olmalı"); return; }
    try {
      await api.post(`/payroll/runs/${month}/unlock`, { reason });
      toast.success("Kilit açıldı");
      run();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Maaş Hesaplama"
        subtitle="Ayı seç → Hesapla → Kontrol Et → Onayla — Muhasebenin tek ekranı" />

      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="bg-slate-950 border-slate-700 rounded-sm w-40" data-testid="salary-month" />
        <Button size="lg" className="bg-blue-600 hover:bg-blue-500 rounded-sm font-bold uppercase tracking-wider"
          onClick={run} disabled={loading} data-testid="salary-calculate-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Calculator className="w-5 h-5 mr-2" />}
          Puantajı Getir ve Maaşları Hesapla
        </Button>
        <Button variant="outline" size="sm" onClick={() => setAdvOpen(true)}
          className="border-slate-700 rounded-sm" data-testid="salary-advance-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Avans Ekle
        </Button>
        <Button variant="outline" size="sm" onClick={() => setBonusOpen(true)}
          className="border-slate-700 rounded-sm" data-testid="salary-bonus-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Prim / Ek Ödeme
        </Button>
        <Button variant="outline" size="sm" onClick={() => downloadReport(month, "xlsx")}
          className="border-emerald-800 text-emerald-300 rounded-sm" data-testid="salary-export-xlsx">
          <FileText className="w-3.5 h-3.5 mr-1" /> Excel
        </Button>
        <Button variant="outline" size="sm" onClick={() => downloadReport(month, "pdf")}
          className="border-rose-800 text-rose-300 rounded-sm" data-testid="salary-export-pdf">
          <FileText className="w-3.5 h-3.5 mr-1" /> PDF
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Badge className={`rounded-sm ${locked ? "bg-rose-950 text-rose-300 border-rose-800" : "bg-emerald-950 text-emerald-300 border-emerald-800"}`}
            data-testid="salary-status-badge">
            {locked ? <><Lock className="w-3 h-3 mr-1 inline" /> Kilitli</> : <>Taslak</>}
          </Badge>
          {!locked ? (
            <Button className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" onClick={approve} data-testid="salary-approve-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Ayı Onayla
            </Button>
          ) : (
            <Button variant="outline" className="border-rose-700 text-rose-300 rounded-sm" onClick={unlock} data-testid="salary-unlock-btn">
              <Unlock className="w-4 h-4 mr-1" /> Kilit Aç
            </Button>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Personel" value={totals.personnel ?? 0} tone="slate" testId="kpi-total-personnel" />
        <Kpi label="Toplam Net Maaş" value={fmtTRY(totals.wage)} tone="blue" testId="kpi-total-wage" />
        <Kpi label="Fazla Mesai" value={fmtTRY(totals.overtime)} tone="emerald" testId="kpi-total-ot" />
        <Kpi label="Prim / Ek Ödeme" value={fmtTRY(totals.bonuses)} tone="emerald" testId="kpi-total-bonus" />
        <Kpi label="Avans" value={fmtTRY(totals.advance)} tone="amber" testId="kpi-total-advance" />
        <Kpi label="TOPLAM ÖDENECEK" value={fmtTRY(totals.net_pay)} tone="orange" big testId="kpi-total-net" />
      </div>

      {/* Table */}
      <Card className="bg-slate-950/60 border-slate-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-800">
            <tr>
              <th className="text-left px-3 py-2">Personel</th>
              <th className="text-right px-3 py-2">Net Maaş</th>
              <th className="text-right px-3 py-2">Eksik Gün</th>
              <th className="text-right px-3 py-2">Fazla Mesai</th>
              <th className="text-right px-3 py-2">Prim</th>
              <th className="text-right px-3 py-2">Avans</th>
              <th className="text-right px-3 py-2">Diğer Kesinti</th>
              <th className="text-right px-3 py-2 font-bold">ÖDENECEK</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.employee.id}
                className="border-b border-slate-900 hover:bg-slate-900/40 cursor-pointer"
                onClick={() => setDetailEmp(r)} data-testid={`salary-row-${r.employee.id}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <EmployeeAvatar emp={r.employee} />
                    <div>
                      <div className="text-slate-100">{r.employee.name}</div>
                      <div className="text-[10px] text-slate-500">{r.employee.department || "—"}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-200">{fmtTRY(r.wage_amount)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-rose-300">
                  {r.missing_days > 0 ? `${r.missing_days} · -${fmtTRY(r.deductions.missing_days)}` : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">
                  {r.overtime_amount > 0 ? `+${fmtTRY(r.overtime_amount)}` : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">
                  {r.extras.bonuses > 0 ? `+${fmtTRY(r.extras.bonuses)}` : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-300">
                  {r.advance_total > 0 ? `-${fmtTRY(r.advance_total)}` : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-rose-300">
                  {r.deductions.manual > 0 ? `-${fmtTRY(r.deductions.manual)}` : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-white font-bold">
                  {fmtTRYExact(r.net_pay)}
                </td>
              </tr>
            ))}
            {(!data || data.rows.length === 0) && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">
                Hesaplama için yukarıdaki butona basın
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {detailEmp && <SalaryDetailDialog row={detailEmp} month={month} onClose={() => setDetailEmp(null)} onChanged={run} locked={locked} />}
      {advOpen && <AdvanceDialog month={month} onClose={() => { setAdvOpen(false); run(); }} />}
      {bonusOpen && <BonusDialog month={month} onClose={() => { setBonusOpen(false); run(); }} />}
    </div>
  );
}

function Kpi({ label, value, tone, big, testId }) {
  const tones = {
    slate:   "border-slate-800 bg-slate-950/60 text-slate-200",
    blue:    "border-blue-900 bg-blue-950/40 text-blue-200",
    emerald: "border-emerald-900 bg-emerald-950/40 text-emerald-200",
    amber:   "border-amber-900 bg-amber-950/40 text-amber-200",
    orange:  "border-blue-800 bg-blue-950/60 text-white",
  };
  return (
    <div data-testid={testId} className={`border rounded-sm p-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div className={`mt-1 font-bold font-mono tabular-nums ${big ? "text-xl" : "text-lg"}`}>{value}</div>
    </div>
  );
}

function SalaryDetailDialog({ row, month, onClose, onChanged, locked }) {
  const [advances, setAdvances] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const load = useCallback(async () => {
    try {
      const [{ data: a }, { data: b }] = await Promise.all([
        api.get("/payroll/advances", { params: { month, employee_id: row.employee.id } }),
        api.get("/payroll/bonuses", { params: { month, employee_id: row.employee.id } }),
      ]);
      setAdvances(a?.items || []);
      setBonuses(b?.items || []);
    } catch { }
  }, [month, row.employee.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-950 border-slate-800 max-w-2xl" data-testid="salary-detail-dialog">
        <DialogHeader>
          <DialogTitle>{row.employee.name} — {month}</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            {row.employee.department || "—"} · {row.employee.position || "—"}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="border border-slate-800 rounded-sm p-3 space-y-1">
            <Row label="Aylık Net Maaş" value={fmtTRY(row.wage_amount)} />
            <Row label="Günlük Ücret" value={fmtTRYExact(row.daily_rate)} />
            <Row label="Saatlik Normal" value={fmtTRYExact(row.hourly_rate_normal)} />
            <Row label="Fazla Mesai Katsayısı" value={`× ${row.overtime_coefficient}`} />
          </div>
          <div className="border border-slate-800 rounded-sm p-3 space-y-1">
            <Row label="Çalışılan Gün" value={`${row.worked_days} gün`} />
            <Row label="Ücretli İzin" value={`${row.paid_leave_days} gün`} />
            <Row label="Ücretsiz İzin" value={`${row.unpaid_leave_days} gün`} tone="amber" />
            <Row label="Rapor" value={`${row.sick_days} gün`} />
            <Row label="Eksik Gün" value={`${row.missing_days} gün`} tone="rose" />
            <Row label="Fazla Mesai" value={`${row.overtime_hours} sa`} tone="emerald" />
          </div>
        </div>

        <div className="border-t border-slate-800 pt-3 space-y-1 text-sm">
          <Row label="Hak Edilen Maaş" value={fmtTRYExact(row.earned)} />
          <Row label="Fazla Mesai" value={`+ ${fmtTRYExact(row.overtime_amount)}`} tone="emerald" />
          <Row label="Prim / Ek Ödeme" value={`+ ${fmtTRYExact(row.extras.bonuses)}`} tone="emerald" />
          <Row label="Avans" value={`- ${fmtTRYExact(row.advance_total)}`} tone="amber" />
          <Row label="Diğer Kesinti" value={`- ${fmtTRYExact(row.deductions.manual)}`} tone="rose" />
          <div className="flex items-center justify-between border-t border-slate-800 pt-2 mt-2">
            <div className="text-xs uppercase tracking-wider text-slate-400">Net Ödenecek</div>
            <div className="text-2xl font-bold font-mono text-white" data-testid="salary-detail-net">{fmtTRYExact(row.net_pay)}</div>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-3">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Bu Ay Avanslar</div>
          {advances.length === 0 && <div className="text-slate-500 text-xs">Kayıt yok</div>}
          {advances.map((a) => (
            <div key={a.id} className="flex items-center justify-between border border-slate-800 rounded-sm px-2 py-1 text-xs mb-1">
              <span className="text-slate-400 font-mono">{a.date}</span>
              <span className="text-amber-300 font-mono">-{fmtTRY(a.amount)}</span>
              <span className="text-slate-400 flex-1 mx-2 truncate">{a.note}</span>
              {!locked && (
                <Button variant="ghost" size="sm" className="text-rose-400 h-6 px-1" data-testid={`del-advance-${a.id}`}
                  onClick={async () => { await api.delete(`/payroll/advances/${a.id}`); toast.success("Silindi"); load(); onChanged?.(); }}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          ))}
          <div className="text-xs uppercase tracking-wider text-slate-400 mt-3 mb-2">Bu Ay Prim / Ek / Kesinti</div>
          {bonuses.length === 0 && <div className="text-slate-500 text-xs">Kayıt yok</div>}
          {bonuses.map((b) => (
            <div key={b.id} className="flex items-center justify-between border border-slate-800 rounded-sm px-2 py-1 text-xs mb-1">
              <Badge className="bg-slate-900 border-slate-700 text-slate-200 rounded-sm text-[9px] uppercase">{b.kind}</Badge>
              <span className={b.amount >= 0 ? "text-emerald-300 font-mono" : "text-rose-300 font-mono"}>
                {b.amount >= 0 ? "+" : ""}{fmtTRY(b.amount)}
              </span>
              <span className="text-slate-400 flex-1 mx-2 truncate">{b.note}</span>
              {!locked && (
                <Button variant="ghost" size="sm" className="text-rose-400 h-6 px-1" data-testid={`del-bonus-${b.id}`}
                  onClick={async () => { await api.delete(`/payroll/bonuses/${b.id}`); toast.success("Silindi"); load(); onChanged?.(); }}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" className="border-slate-700 rounded-sm" onClick={onClose}>Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, tone }) {
  const t = tone === "rose" ? "text-rose-300" : tone === "amber" ? "text-amber-300" : tone === "emerald" ? "text-emerald-300" : "text-slate-100";
  return (
    <div className="flex items-center justify-between">
      <div className="text-slate-400 text-xs">{label}</div>
      <div className={`font-mono tabular-nums ${t}`}>{value}</div>
    </div>
  );
}

function AdvanceDialog({ month, onClose }) {
  const [emps, setEmps] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [dateStr, setDateStr] = useState(todayIso());
  const [amount, setAmount] = useState(0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/employees").then((r) => setEmps(r.data || [])); }, []);
  const save = async () => {
    if (!employeeId || !amount) { toast.error("Personel ve tutar zorunlu"); return; }
    try {
      setSaving(true);
      await api.post("/payroll/advances", { employee_id: employeeId, month, date: dateStr, amount: Number(amount), note });
      toast.success("Avans eklendi");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-950 border-slate-800" data-testid="advance-dialog">
        <DialogHeader><DialogTitle>Avans Ekle · {month}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Personel</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="advance-employee"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-700">
                {emps.filter((e) => e.status === "aktif").map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Tarih</Label>
              <Input type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)}
                className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="advance-date" />
            </div>
            <div>
              <Label className="text-xs">Tutar (₺)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="advance-amount" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Açıklama</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
              className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="advance-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-slate-700 rounded-sm" onClick={onClose}>Vazgeç</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" onClick={save} disabled={saving} data-testid="advance-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BonusDialog({ month, onClose }) {
  const [emps, setEmps] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [kind, setKind] = useState("prim");
  const [amount, setAmount] = useState(0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/employees").then((r) => setEmps(r.data || [])); }, []);
  const save = async () => {
    if (!employeeId || !amount || !note.trim()) { toast.error("Personel, tutar ve açıklama zorunlu"); return; }
    try {
      setSaving(true);
      await api.post("/payroll/bonuses", { employee_id: employeeId, month, kind, amount: Number(amount), note });
      toast.success("Kaydedildi");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-950 border-slate-800" data-testid="bonus-dialog">
        <DialogHeader><DialogTitle>Prim / Ek Ödeme / Kesinti · {month}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Personel</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="bonus-employee"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-700">
                {emps.filter((e) => e.status === "aktif").map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Tür</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="bonus-kind"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-700">
                  <SelectItem value="prim">Prim</SelectItem>
                  <SelectItem value="ikramiye">İkramiye</SelectItem>
                  <SelectItem value="yol">Yol</SelectItem>
                  <SelectItem value="yemek">Yemek</SelectItem>
                  <SelectItem value="diger_ek">Diğer Ek Ödeme</SelectItem>
                  <SelectItem value="diger_kesinti">Diğer Kesinti</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tutar (₺)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="bonus-amount" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Açıklama (zorunlu)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="ör. Temmuz satış primi"
              className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="bonus-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-slate-700 rounded-sm" onClick={onClose}>Vazgeç</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" onClick={save} disabled={saving} data-testid="bonus-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==========================================================================
// 3) EmployeeWageCard — EmployeeProfile içinde kullanılır
// ==========================================================================
export function EmployeeWageCard({ employeeId }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ wage_amount: 0, wage_type: "aylik", overtime_enabled: true, overtime_coefficient: 1.5, effective_from: todayIso(), note: "" });
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/payroll/employees/${employeeId}/wage`);
      setData(data);
      setForm({
        wage_amount: data.current.wage_amount || 0,
        wage_type: data.current.wage_type || "aylik",
        overtime_enabled: !!data.current.overtime_enabled,
        overtime_coefficient: data.current.overtime_coefficient || 1.5,
        effective_from: data.current.wage_effective_from || todayIso(),
        note: "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      setSaving(true);
      await api.post(`/payroll/employees/${employeeId}/wage`, form);
      toast.success("Ücret kaydedildi");
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  if (!data) return null;
  return (
    <Card className="bg-slate-950/60 border-slate-800 p-4 space-y-3" data-testid="emp-wage-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400">Mevcut Ücret</div>
          <div className="text-2xl font-bold text-white font-mono tabular-nums mt-1" data-testid="emp-wage-current">
            {fmtTRY(data.current.wage_amount)}
            <span className="text-xs text-slate-500 ml-2">/ {data.current.wage_type === "gunluk" ? "gün" : "ay"}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Fazla mesai: {data.current.overtime_enabled ? `× ${data.current.overtime_coefficient}` : "kapalı"}
            {data.current.wage_effective_from && <> · Geçerlilik: {data.current.wage_effective_from}</>}
          </div>
        </div>
        <Button variant="outline" size="sm" className="border-slate-700 rounded-sm" onClick={() => setShowForm(!showForm)}
          data-testid="emp-wage-edit-btn">
          <Edit3 className="w-3.5 h-3.5 mr-1" /> {showForm ? "İptal" : "Güncelle"}
        </Button>
      </div>
      {showForm && (
        <div className="border-t border-slate-800 pt-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Tutar (₺)</Label>
              <Input type="number" value={form.wage_amount} onChange={(e) => setForm({ ...form, wage_amount: Number(e.target.value) })}
                className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="emp-wage-amount" />
            </div>
            <div>
              <Label className="text-xs">Tip</Label>
              <Select value={form.wage_type} onValueChange={(v) => setForm({ ...form, wage_type: v })}>
                <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="emp-wage-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-700">
                  <SelectItem value="aylik">Aylık</SelectItem>
                  <SelectItem value="gunluk">Günlük</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2">
              <Switch checked={form.overtime_enabled} onCheckedChange={(v) => setForm({ ...form, overtime_enabled: v })} data-testid="emp-wage-ot-enabled" />
              <Label className="text-xs">Fazla Mesai Hesaplansın</Label>
            </div>
            <div>
              <Label className="text-xs">Katsayı</Label>
              <Input type="number" step="0.1" value={form.overtime_coefficient} onChange={(e) => setForm({ ...form, overtime_coefficient: Number(e.target.value) })}
                className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="emp-wage-ot-coef" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Geçerlilik Tarihi</Label>
              <Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="emp-wage-effective" />
            </div>
            <div>
              <Label className="text-xs">Açıklama</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="ör. Zam"
                className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="emp-wage-note" />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" data-testid="emp-wage-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Kaydet</>}
            </Button>
          </div>
        </div>
      )}
      {data.history?.length > 0 && (
        <div className="border-t border-slate-800 pt-3">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Maaş Geçmişi ({data.history.length})</div>
          <div className="space-y-1 text-xs max-h-40 overflow-y-auto">
            {data.history.map((h) => (
              <div key={h.id} className="flex items-center justify-between border border-slate-800 rounded-sm px-2 py-1 font-mono">
                <span className="text-slate-400">{h.effective_from}</span>
                <span className="text-white">{fmtTRY(h.amount)} <span className="text-slate-500 text-[9px]">{h.wage_type}</span></span>
                <span className="text-slate-500 flex-1 mx-2 truncate">{h.note || h.changed_by_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
